import { fireBeacon, observeViewability, sendBidBatch } from './beacon.js';
// Build-identity metadata (see core/version.js) - logged once per boot() call
// so a live page's DevTools console answers "which commit is this CDN file
// actually built from?" without curling and grepping the deployed bundle by
// hand. `core/version.js` is itself a leaf (only imports config/env.js), so
// this doesn't touch the standing `core -/-> widgets`/`core -/-> protocol`
// rule at all.
import { BUILD_ID, SDK_BUILT_AT } from './version.js';
import { config } from '../config/env.js';
// SDKEvent lives in `src/core/` too (a sibling of this file, not
// `widgets/*`/`protocol/*`) - importing it doesn't touch the standing
// `core -/-> widgets`/`core -/-> protocol` rule at all, and it's the exact
// vocabulary VideoWidget's own public `widget.on()` speaks (see
// wireVideoBeacon() below), so using the real catalog instead of raw string
// literals keeps this wired to the authoritative source.
import { SDKEvent } from './SDKEvents.js';
// The page's GAM + Prebid connection (Atlas ch.4 B3-B10). Lives in
// `src/headerbidding/`, which imports nothing from `widgets/` or `protocol/`,
// so the standing `core -/-> widgets`/`core -/-> protocol` rule still holds.
import { PageAdServer, PREBID_BUNDLE_PATH } from '../headerbidding/PageAdServer.js';
import { readCachedConfig, writeCachedConfig } from '../headerbidding/boot.js';
// Ad refresh for a filled GAM display slot (Atlas ch.8, ADR-033) - same leaf rule.
import { SlotRefresher } from '../headerbidding/SlotRefresher.js';
// The page's AdSense connection (ADR-032) - same `headerbidding/` leaf rule.
import { AdSensePage, createAdSenseIns, isAdSenseConfig } from '../headerbidding/AdSense.js';
import { createFloatingLayout, FLOATING_WIDGET_Z_INDEX } from './floatingLayout.js';
// A `core/` leaf (pure data) - same reasoning as SDKEvent above. Imported
// from its own module, not defaults.js, to keep the boot bundle lean.
import { INTEXT_LINK_STYLES } from './config/inTextLinkStyles.js';
import { RSOC_STYLES } from './config/rsocStyles.js';

// Implements "Boot" (Atlas ch.4, B1+B2) - the two calls a page fires
// together on every load: B1 (`GET /v1/sdk-config/{siteKey}`) loads only the
// widget code the page actually needs, and B2 (`GET /api/v1/config`) mints a
// telemetry bearer + volume levers for a future telemetry-sending phase to
// consume (boot() only fetches and exposes it here - see the follow-on note
// on fetchTelemetryAuth() below). Deliberately scoped to these two
// documented, currently-live endpoints only - it does not touch `src/api/`'s
// configureBackend() telemetry layer, which is built against a different
// (older, since-superseded) backend contract.
//
// Never imports from `src/widgets/*` or `src/api/*` - it only knows widget
// TYPE STRINGS and DOM script-tag mechanics, preserving the standing
// `core -/-> widgets`/`core -/-> protocol` dependency rule the rest of
// `core/` follows.
//
// Format -> widget mapping is fixed and deliberately small: a
// `units[].format` value that isn't in this table is never this SDK's
// concern, regardless of demand. `in_text`, `contextual_commerce`, `rsoc`
// and `chat_sponsored` once belonged to a separate "house tag" package;
// ADR-030/ADR-031 brought them into the SDK - see mountInText()/
// mountCommerce()/mountRelatedSearch() and the read-aloud section below
// (`chat_sponsored` is the read-aloud widget's ask box, not a widget of its
// own). This is format-based only, not demand-based - an `audio` unit
// (demand `vast`) and an `outstream_video` unit (demand `house`) both trigger
// their widget's load; nothing here reads `unit.demand`.
//
// `native` and `corner_slider` both map onto `display` too (not a fourth/
// fifth widget type/script) - an in-article native ad, a banner, and a
// corner slider are all rendered by the exact same DisplayWidget, just
// placed differently (see renderNativeUnit()/renderBannerUnit()/
// mountCornerSlider() below). `renderUnit()` branches on `unit.format`
// itself to tell them apart once it already knows `type === 'display'`.
//
// `audio` maps to `readAloud`, NOT a standalone AudioWidget player - this
// was a real Milestone 8.1 decision, ported from the same live reference
// site (jobopting.com) this whole boot.js file already matches: an `audio`
// format unit's own `positions`/`vastTags` become the "Listen & Explore"
// widget's ad breaks (see mountReadAloud() below), never a bare audio ad
// player rendered into `unit.divId`. There is deliberately no `'audio'`
// widget-type entry left in WIDGET_TYPE_TO_GLOBAL any more - nothing in this
// file constructs a standalone AudioWidget any longer (ReadAloudWidget's own
// bundle already carries AudioWidget's code internally, for its disposable
// per-ad-break instances - see widgets/readAloud/ads/ReadAloudAdBreakRunner.js
// - so boot() never needs to separately fetch playbid-audio.umd.js just to
// satisfy an `audio` format unit).
const FORMAT_TO_WIDGET_TYPE = {
  outstream_video: 'video',
  audio: 'readAloud',
  banner: 'display',
  native: 'display',
  corner_slider: 'display',
  in_text: 'inText',
  contextual_commerce: 'commerce',
  rsoc: 'relatedSearch',
  chat_sponsored: 'readAloud',
};

const WIDGET_TYPE_TO_GLOBAL = {
  video: 'VideoWidget',
  display: 'DisplayWidget',
  readAloud: 'ReadAloudWidget',
  inText: 'InTextWidget',
  commerce: 'CommerceWidget',
  relatedSearch: 'RelatedSearchWidget',
};

// The bundle a widget type ships in, when it is not `playbid-{type}.umd.js`.
// The contextual widgets share one `playbid-contextual.umd.js` (they share
// their whole orchestrator/client/event catalog), so a page with in-text,
// commerce AND related searches still fetches one script.
const WIDGET_TYPE_TO_BUNDLE = {
  inText: 'contextual',
  commerce: 'contextual',
  relatedSearch: 'contextual',
};

function bundleFor(type) {
  return WIDGET_TYPE_TO_BUNDLE[type] ?? type;
}

// Captured at module top-level evaluation time, not inside boot() - this is
// the only point at which `document.currentScript` is valid for a classic
// (non-module) <script> tag; by the time an async function's body actually
// runs, the browser has moved on to other scripts and `document.currentScript`
// has already reverted to null. A <script type="module"> consumer gets
// `document.currentScript === null` by spec even here, so `cdnBase` becomes
// required in that case - the primary supported path is a plain UMD
// <script src="..."> embed, matching every existing precedent in this
// codebase family (e.g. playbid-sdk-plugin's loader.js).
const BOOT_SCRIPT = typeof document !== 'undefined' ? document.currentScript : null;

// The boot-config endpoint and the sibling widget bundles resolve from two
// DIFFERENT bases, not one - conflating them was a real bug caught by testing
// against a real consuming project. `/v1/sdk-config/{siteKey}` is documented
// as reachable at the bare origin of wherever the SDK script is served
// ("same origin as the tag"). Widget bundles are never guaranteed to sit at
// that bare origin, though - this SDK's own deploy pipeline
// (scripts/deploy.mjs) puts every build under a `/staging/` or `/prod/` path
// prefix, and any consumer nesting the SDK under a subpath (e.g.
// `node_modules/playbid-sdk/`) would see the same thing. Sibling widget
// bundles are always built and deployed alongside the boot bundle itself
// (scripts/build.mjs), so resolving them relative to the boot script's own
// directory - not the bare origin - is what actually matches how this SDK
// is shipped.
//
// `apiBase` and `cdnBase` are also independently overridable, not just
// jointly - a real second case needed this: a browser extension injects the
// boot script from `chrome-extension://<id>/...`, which is where the sibling
// widget bundles genuinely live too, but the real `/v1/sdk-config` endpoint
// obviously isn't served by the extension itself. `apiBase` lets that case
// pin the config fetch to the real backend while `cdnBase` still auto-infers
// (or is separately overridden) for widget-script loading.
function resolveBases({ apiBase, cdnBase } = {}) {
  const inferred = (() => {
    if (!BOOT_SCRIPT?.src) return { origin: null, dir: null };
    try {
      const url = new URL(BOOT_SCRIPT.src);
      return { origin: url.origin, dir: url.href.slice(0, url.href.lastIndexOf('/')) };
    } catch {
      return { origin: null, dir: null };
    }
  })();

  const strip = (base) => base?.replace(/\/$/, '') ?? null;

  return {
    configOrigin: strip(apiBase) ?? strip(cdnBase) ?? inferred.origin,
    scriptBase: strip(cdnBase) ?? inferred.dir,
  };
}

// Viewport-width breakpoints ported from playbid-sdk-plugin's own
// ad-units.js device() helper, for consistency across both products -
// neither product's own spec documents these as authoritative elsewhere, so
// matching the sibling package's already-shipped thresholds is the least
// surprising choice. Checked once, at render time - not re-evaluated on
// resize/rotate, the same one-shot semantics ad-units.js's own device gate
// uses (unlike the sticky banner's viewport gate above, which genuinely
// does need to react live to a resize).
function currentDevice() {
  const width = (typeof window !== 'undefined' && window.innerWidth) || 1024;
  if (width <= 600) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}

// `unit.devices` is an ALLOW-list (e.g. `["desktop","tablet"]` means mobile
// visitors never see it) - absent/non-array means no restriction, matching
// every other "field missing -> unrestricted" default in this file
// (dismissible, delayMs, placement.sticky). An explicit empty array excludes
// every device, matching the literal "not in the array" reading.
function deviceAllowed(unit) {
  return !Array.isArray(unit?.devices) || unit.devices.includes(currentDevice());
}

function widgetTypesFor(units) {
  const types = new Set();
  for (const unit of units ?? []) {
    // A unit excluded for this device shouldn't even cost the publisher the
    // widget script it would have needed - the same "don't load what this
    // page doesn't need" principle boot() was built around in the first
    // place.
    if (!deviceAllowed(unit)) continue;
    const type = FORMAT_TO_WIDGET_TYPE[unit?.format];
    if (type) types.add(type);
  }
  return types;
}

function isAlreadyLoaded(type) {
  const globalName = WIDGET_TYPE_TO_GLOBAL[type];
  return typeof window !== 'undefined' && typeof window.PlaybidSDK?.[globalName] === 'function';
}

// --- Auto-render: turns a boot()'d unit into a live widget with zero
// publisher-authored JS ------------------------------------------------
//
// A publisher's entire integration is meant to be the CDN <script> tag
// itself - no callback, no widget-construction code, and no pre-placed
// <div> on their page for most unit types either. `banner` (non-sticky) and
// `native` still read `unit.divId` and find that element (publisher-placed,
// or - for `native` - the article wrapper the publisher marks) before
// constructing/initializing/loading the right widget into it - real
// per-unit creative resolution for those isn't wired yet (see below), and a
// publisher-owned slot is still how GAM-style inventory is conventionally
// placed. Every self-injected unit type (sticky banner, corner slider,
// sticky video, in-content video - see createInContentVideoContainer() -
// AND now read-aloud - see mountReadAloud()) goes further: the SDK creates
// its own container element rather than expecting the publisher to have
// placed one, since these need layout mechanics (fixed positioning, space
// reservation, host-header avoidance, or - for in-content video/read-aloud -
// finding a real content anchor) no static publisher div could reliably
// provide anyway. All paths render entirely through the runtime
// `window.PlaybidSDK.*` globals the widget scripts just attached (never a
// static import - boot() still never imports from `src/widgets/*`,
// preserving the standing `core -/-> widgets` rule; this is the exact same
// runtime-lookup trick `isAlreadyLoaded()` already uses).
//
// Display (`banner`/`native`/`corner_slider`) and outstream video units with
// `demand: "gam"` are real inventory: each placement becomes one GPT slot on
// `unit.gam.adUnitPath`, auctioned through Prebid with `unit.bidders` and
// decided by the publisher's GAM (see PageAdServer). Display units with
// `demand: "adsense"` use the very same mounts but fill with one AdSense
// `<ins>` instead (see loadAdSenseDisplay(), ADR-032). A unit with any other
// demand has nothing this page can request, so it renders nothing - never a
// stand-in creative a reader could mistake for an ad. Outstream video with
// the publisher's own `vastTags` plays those directly (`demand: "vast"`), and
// read-aloud's ad breaks likewise run only on real `vastTags`.

// B10 for `outstream_video` - a different event vocabulary from the display
// slots' slotBeacons() entirely: VideoWidget's public API is its own EventBus
// directly (widget.on(SDKEvent.X, handler)), not a PublisherEventBridge the
// way DisplayWidget's `creative:*` events are - see the exact
// PlayerEvent->SDKEvent bridge in widgets/video/core/VideoWidget.js's
// #bindSdkEvents(). `unitType` is always `'video'`, the one B10 enum value
// with a dedicated slot (banner/native/corner_slider all share `'banner'`).
//
// `impression`/`viewable` use a 2000ms MRC dwell, not the 1000ms every other
// unit type uses - B10's own text draws this distinction explicitly: "50%
// of pixels for 1 s, 2 s for video". `vast_start`/`vast_first_quartile`/
// `vast_midpoint`/`vast_third_quartile`/`vast_complete` mirror
// TrackingManager's own SDKEvent.PLAYBACK_PROGRESS quartile-crossing math
// exactly (protocol/vast/tracking/manager/TrackingManager.js) - duplicated
// here rather than imported, since boot.js may never import from
// `protocol/vast/`. `vast_skip` is never fired - skip UI/tracking is
// explicitly post-MVP (ADR-028), so there's nothing to report yet. `error`
// covers both a playback failure (SDKEvent.PLAYER_ERROR) and a VAST
// load/parse failure (SDKEvent.VAST_ERROR) - deduplicated so a failure that
// trips both only reports once.
const VAST_QUARTILE_THRESHOLDS = [
  { ratio: 0.25, event: 'vast_first_quartile' },
  { ratio: 0.5, event: 'vast_midpoint' },
  { ratio: 0.75, event: 'vast_third_quartile' },
];
const VIDEO_VIEWABILITY_DWELL_MS = 2000;

function wireVideoBeacon(container, unit, { configOrigin, siteKey }, widget, { winningBid } = {}) {
  if (typeof crypto === 'undefined' || !crypto.randomUUID) return;
  if (typeof widget?.on !== 'function') return;

  const basePayload = {
    apiKey: siteKey,
    requestId: crypto.randomUUID(),
    unitType: 'video',
    slot: slotOf(unit),
  };
  const fire = (event, extra) => fireBeacon(configOrigin, { ...basePayload, event, ...extra });

  let started = false;
  let errored = false;
  const firedQuartiles = new Set();

  widget.on(SDKEvent.PLAYBACK_STARTED, () => {
    // VideoWidget itself only ever emits this once per load (a resume emits
    // PLAYBACK_RESUMED instead), so this guard is defensive redundancy, not
    // load-bearing - cheap insurance against that changing later.
    if (started) return;
    started = true;
    fire('vast_start');
  });
  widget.on(SDKEvent.PLAYBACK_PROGRESS, ({ currentTime, duration } = {}) => {
    if (!duration) return;
    const ratio = currentTime / duration;
    VAST_QUARTILE_THRESHOLDS.forEach(({ ratio: threshold, event }) => {
      if (ratio >= threshold && !firedQuartiles.has(event)) {
        firedQuartiles.add(event);
        fire(event);
      }
    });
  });
  widget.on(SDKEvent.PLAYBACK_COMPLETED, () => fire('vast_complete'));
  widget.on(SDKEvent.AD_CLICK, () => fire('click'));
  const fireErrorOnce = () => {
    if (errored) return;
    errored = true;
    fire('error');
  };
  widget.on(SDKEvent.PLAYER_ERROR, fireErrorOnce);
  widget.on(SDKEvent.VAST_ERROR, fireErrorOnce);

  observeViewability(
    container,
    () => {
      fire('viewable');
      fire('impression', revenueFields(winningBid?.()));
    },
    VIDEO_VIEWABILITY_DWELL_MS,
  );
}

// --- GAM-served units: one GPT slot per placement ---------------------------
//
// Everything below is shared by every GAM-demand mount (sticky banner, corner
// slider, native, a publisher-placed banner, outstream video). The mount
// functions own layout; these own the slot, its sizes and its B10 beacons.

/** The reporting grain: `banner.top`, not `banner` - a sticky banner has four
 *  positions and each is its own GAM ad unit, so reporting them as one slot
 *  made them impossible to tell apart. Falls back to the format for a unit
 *  that carries no key. */
function slotOf(unit) {
  return unit?.key ?? unit?.format;
}

function isGamUnit(unit) {
  return unit?.demand === 'gam' && typeof unit?.gam?.adUnitPath === 'string' && unit.gam.adUnitPath.length > 0;
}

/** `demand: "adsense"` with a usable publisher id and ad slot (ADR-032). */
function isAdSenseUnit(unit) {
  return unit?.demand === 'adsense' && isAdSenseConfig(unit?.adsense);
}

/** A display unit this page can fill: GAM (GPT + Prebid) or AdSense. Both go
 *  through the same mounts; only the fill step differs (see loadDisplay()). */
function isDisplayDemandUnit(unit) {
  return isGamUnit(unit) || isAdSenseUnit(unit);
}

/** The sizes this device may request. `sizesByDevice` is what the dashboard
 *  set per device; `sizes` (the union) is the fallback for a unit without it. */
function deviceSizes(unit) {
  const byDevice = unit?.sizesByDevice?.[currentDevice()];
  const list = Array.isArray(byDevice) && byDevice.length ? byDevice : unit?.sizes;
  return Array.isArray(list) ? list.filter((s) => Array.isArray(s) && s[0] > 0 && s[1] > 0) : [];
}

/** Revenue dimensions for an impression won through Prebid (B10). Absent when
 *  AdX or direct-sold won: GAM never tells the page a price, and a guess would
 *  be worse than nothing (Atlas ch.4 B9). */
function revenueFields(bid) {
  if (!bid || !(Number(bid.cpm) > 0)) return {};
  const out = {
    // micro-USD for THIS impression: CPM / 1000 impressions * 1e6.
    revenueMicros: Math.round(Number(bid.cpm) * 1000),
  };
  if (typeof bid.bidderCode === 'string') out.ssp = bid.bidderCode.slice(0, 32);
  const w = Number(bid.width);
  const h = Number(bid.height);
  if (w > 0 && h > 0) out.size = `${w}x${h}`;
  else if (typeof bid.size === 'string' && /^\d+x\d+$/.test(bid.size)) out.size = bid.size;
  const pb = bid.adserverTargeting?.hb_pb ?? bid.pbCg;
  if (typeof pb === 'string' && /^\d{1,4}\.\d{2}$/.test(pb)) out.hbPb = pb;
  return out;
}

const GAM_CREATIVE_FORMAT = 'gam';

/** DisplayWidget renderer for a GAM slot: an empty element with the GPT id,
 *  which GPT paints into when GAM answers. The widget keeps everything else
 *  it already does - the close button, the wrapper, the lifecycle. */
function renderGamSlot(wrapper, creative) {
  const el = document.createElement('div');
  el.id = creative.metadata.elementId;
  el.className = 'playbid-gam-slot';
  el.style.width = '100%';
  el.style.height = '100%';
  wrapper.appendChild(el);
  return Promise.resolve(el);
}

function gamCreative(unit, elementId, [width, height]) {
  return {
    id: elementId,
    format: GAM_CREATIVE_FORMAT,
    width,
    height,
    // Not an image: the GAM path the slot requests, for diagnostics only.
    imageUrl: `gam:${unit.gam.adUnitPath}`,
    metadata: { elementId, adUnitPath: unit.gam.adUnitPath },
  };
}

/** A click inside a GAM creative lands in a cross-origin iframe the page can
 *  never see. The iframe taking focus is the one signal there is, so a window
 *  blur while focus sits in this slot's iframe counts as one click. */
function watchIframeClicks(el, onClick) {
  let last = 0;
  const onBlur = () => {
    setTimeout(() => {
      const active = document.activeElement;
      if (!active || active.tagName !== 'IFRAME' || !el.contains(active)) return;
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      onClick();
    }, 0);
  };
  window.addEventListener('blur', onBlur);
  return () => window.removeEventListener('blur', onBlur);
}

/** B10 for a display slot. `viewable` and `impression` fire only once the ad
 *  server has FILLED the slot and it has then been viewable (MRC: 50% for
 *  1 s) - an empty slot is not an impression, however long it sits on screen.
 *  The impression carries the Prebid winner's revenue when there was one, and
 *  `ssp` = `source` for a slot no Prebid bid can win (AdSense: `"adsense"`). */
function slotBeacons(unit, { configOrigin, siteKey }, unitType, { dwellMs, source } = {}) {
  let won = null;
  let active = true;
  const canFire = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
  const base = canFire ? { apiKey: siteKey, requestId: crypto.randomUUID(), unitType, slot: slotOf(unit) } : null;
  const fire = (event, extra) => {
    if (base && active) fireBeacon(configOrigin, { ...base, event, ...extra });
  };
  return {
    won(bid) {
      won = bid;
    },
    winningBid: () => won,
    /** Returns `stop()`, releasing the viewability observer and click watch. */
    filled(el) {
      const stopViewability = observeViewability(
        el,
        () => {
          fire('viewable');
          fire('impression', source ? { ...revenueFields(won), ssp: source } : revenueFields(won));
        },
        dwellMs,
      );
      const stopClicks = watchIframeClicks(el, () => fire('click'));
      return () => {
        stopViewability();
        stopClicks();
      };
    },
    /** Stop reporting for this slot (a video win reports through its player). */
    handOff() {
      active = false;
    },
  };
}

/** Formats whose GAM slot refreshes (Atlas ch.8 R7): display placements a
 *  reader keeps on screen. Native sits in the article and is read past;
 *  outstream video, audio, house, vast and AdSense never refresh this way. */
const REFRESHABLE_FORMATS = new Set(['banner', 'corner_slider']);

/** The publisher's refresh interval for a GAM display unit, or 0 for none.
 *  A value under the 30 s floor is raised to it by RefreshController (R2),
 *  never treated as "off": the dashboard asked for refresh. */
function refreshMsOf(unit) {
  if (!isGamUnit(unit) || !REFRESHABLE_FORMATS.has(unit.format)) return 0;
  const ms = Number(unit.refreshMs);
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

/**
 * Render one GAM slot into `widget` (a DisplayWidget already initialized on
 * its container) and register it with the page's ad server.
 *
 * `onEmpty` runs when GAM has nothing (or the slot could not be requested):
 * the mount removes its container, because an empty ad frame that reserves
 * page space is worse than no frame. `onFilled` receives the size GAM served,
 * which can be any of the requested sizes.
 *
 * Refresh (ADR-033): a `banner`/`corner_slider` unit with `refreshMs` starts
 * a SlotRefresher once GAM first fills it. Each refresh is a new auction for
 * this slot only (`adServer.refresh(elementId)`), and GAM's answer comes back
 * through the same `onRender` as the first: a fill re-sizes the container and
 * starts a NEW set of B10 beacons (new requestId, that auction's winning bid),
 * an empty answer collapses the container exactly as an empty first answer
 * does. The refresher, the viewability observer and the click watch are
 * released on collapse, dismiss and `widget:destroyed`.
 */
function loadGamDisplay(widget, unit, bases, { elementId, sizes, box, unitType, onEmpty, onFilled }) {
  // One beacon set per GAM impression: replaced at each refresh, so a
  // refreshed fill reports under its own requestId with its own winner.
  let beacons = slotBeacons(unit, bases, unitType);
  let stopBeacons = () => {};
  let refresher = null;
  const refreshMs = refreshMsOf(unit);
  const release = () => {
    stopBeacons();
    stopBeacons = () => {};
    refresher?.stop();
  };
  try {
    widget.registerFormatRenderer(GAM_CREATIVE_FORMAT, renderGamSlot);
  } catch (error) {
    console.warn(`[PlaybidSDK] boot(): cannot render GAM unit "${unit?.key}"`, error);
    onEmpty?.();
    return;
  }
  if (typeof widget.on === 'function') {
    widget.on('widget:destroyed', release);
    widget.on('creative:dismissed', release);
  }
  const startRefresh = (el, size) => {
    if (!refreshMs || refresher || !el) return;
    refresher = new SlotRefresher({
      element: el,
      config: { enabled: true, intervalMs: refreshMs },
      areaPx: Array.isArray(size) ? size[0] * size[1] : 0,
      refresh: () => {
        // The new auction's bidWon lands on the new set; the old creative's
        // observer goes with the creative GPT is about to replace.
        stopBeacons();
        stopBeacons = () => {};
        beacons = slotBeacons(unit, bases, unitType);
        return bases.adServer.refresh(elementId);
      },
      onDiagnostic: (message, detail) => console.debug(`[PlaybidSDK] ${unit?.key}: ${message}`, detail ?? ''),
    });
    refresher.start();
  };
  bases.adServer.register({
    slot: slotOf(unit),
    elementId,
    adUnitPath: unit.gam.adUnitPath,
    sizes,
    bidders: unit.bidders ?? [],
    floorCpm: unit.floorCpm,
    onBidWon: (bid) => beacons.won(bid),
    onRender: (r) => {
      if (r.isEmpty) {
        release();
        onEmpty?.(r.reason);
        return;
      }
      const el = document.getElementById(elementId);
      const wrapper = el?.parentElement;
      if (wrapper && Array.isArray(r.size) && r.size[0] > 0 && r.size[1] > 0) {
        // The wrapper was sized to the smallest request; GAM may serve any
        // requested size, so it follows what actually arrived.
        wrapper.style.width = `${r.size[0]}px`;
        wrapper.style.maxWidth = '';
        wrapper.style.aspectRatio = '';
        wrapper.style.height = `${r.size[1]}px`;
      }
      if (el) {
        stopBeacons();
        stopBeacons = beacons.filled(el);
      }
      onFilled?.(r);
      startRefresh(el, r.size ?? box);
    },
  });
  widget.load({ creative: gamCreative(unit, elementId, box) });
}

const ADSENSE_CREATIVE_FORMAT = 'adsense';

/**
 * Render one AdSense slot into `widget` (ADR-032). Same contract as
 * loadGamDisplay() - the mounts call either through loadDisplay() and keep
 * all their layout - but the fill is one `<ins class="adsbygoogle">` sized to
 * `box` (the size the container reserves; AdSense serves one fixed size per
 * `<ins>`), pushed once, and read back from its `data-ad-status`.
 *
 * Never GPT, never Prebid, never a refresh: AdSense policy forbids
 * programmatic refresh, so `unit.refreshMs` is ignored even if present.
 * `onEmpty` runs on `unfilled`, on no answer within ADSENSE_STATUS_TIMEOUT_MS,
 * or when the push fails; `onFilled` receives `{ size: box }`.
 */
function loadAdSenseDisplay(widget, unit, bases, { elementId, box, unitType, onEmpty, onFilled }) {
  const beacons = slotBeacons(unit, bases, unitType, { source: 'adsense' });
  const { client, slot } = unit.adsense;
  let stopFill = () => {};
  let stopBeacons = () => {};
  const release = () => {
    stopFill();
    stopBeacons();
  };
  try {
    widget.registerFormatRenderer(ADSENSE_CREATIVE_FORMAT, (wrapper) => {
      const ins = createAdSenseIns(document, { id: elementId, client, slot, size: box });
      wrapper.appendChild(ins);
      stopFill = bases.adSense.fill(ins, {
        client,
        onFilled: () => {
          stopBeacons = beacons.filled(ins);
          onFilled?.({ size: box });
        },
        onUnfilled: (reason) => onEmpty?.(reason),
      });
      return Promise.resolve(ins);
    });
  } catch (error) {
    console.warn(`[PlaybidSDK] boot(): cannot render AdSense unit "${unit?.key}"`, error);
    onEmpty?.();
    return;
  }
  // Whatever ends the widget (no fill, a page teardown) releases the status
  // observer, its timer and the viewability gate.
  if (typeof widget.on === 'function') widget.on('widget:destroyed', release);
  widget.load({
    creative: {
      id: elementId,
      format: ADSENSE_CREATIVE_FORMAT,
      width: box[0],
      height: box[1],
      // Not an image: the AdSense slot, for diagnostics only.
      imageUrl: `adsense:${client}/${slot}`,
      metadata: { elementId, client, slot },
    },
  });
}

/** The fill step every display mount shares: GAM or AdSense, by `demand`. */
function loadDisplay(widget, unit, bases, opts) {
  if (isAdSenseUnit(unit)) loadAdSenseDisplay(widget, unit, bases, opts);
  else loadGamDisplay(widget, unit, bases, opts);
}

/** The smallest requested size - what a slot reserves before GAM answers. */
function smallestSize(sizes) {
  return sizes.reduce((best, s) => (!best || s[0] * s[1] < best[0] * best[1] ? s : best), null);
}

/** Destroy a widget without letting its teardown reach the page. */
function safeDestroy(widget) {
  try {
    widget?.destroy?.();
  } catch {
    // Already destroyed, or never initialized - nothing left to release.
  }
}

// --- Sticky banner: the SDK injects its own container - no publisher div
// required ------------------------------------------------------------
//
// Ports playbid-sdk-plugin's own already-shipped ad-units.js renderBanner()
// mechanics exactly (verified live against jobopting.com/job-search-2026.html,
// which runs that code: <html> carries padding-top/padding-bottom exactly
// matching the fixed banner heights, confirming real page-squeeze, not
// overlay) - reusing DisplayWidget for the actual ad content (same
// dismissible/delayMs/B10-beacon machinery already built). Page squeeze,
// shrink-to-fit, rail gutters and collision avoidance are all owned by the
// shared floatingLayout() (see floatingLayout.js) rather than this section.
//
// Only unit.placement?.sticky === true takes this path - every sticky
// banner unit observed live carries it (`placement: {mode:"banner",
// side:"top", sticky:true}`). A future non-sticky `banner` unit (if one
// ever exists) falls through to the plain pre-placed-divId path
// (renderBannerUnit(), below) unchanged.
// Every size this device may request for one side, shaped right for it
// (wide-short for top/bottom, narrow-tall for the rails). Reads the
// per-device list (deviceSizes()): `unit.sizes` is the union across devices,
// so picking from it put a 320x50 phone banner on a desktop page.
function bannerSizeCandidates(unit, side) {
  const isSideRail = side === 'left' || side === 'right';
  return deviceSizes(unit).filter(([w, h]) => (isSideRail ? h > w : w > h));
}

// Best-effort heuristic (ported from ad-units.js's topHeaderOffset()) so a
// top sticky banner stacks BELOW an existing host sticky header instead of
// covering it: scans for a fixed/sticky element pinned near the very top,
// reasonably short, and near full-width - the same signature a sticky site
// header has and a random fixed widget usually doesn't.
function topHeaderOffset() {
  if (typeof document === 'undefined') return 0;
  let offset = 0;
  document.querySelectorAll('body *').forEach((el) => {
    if (el.closest('.playbid-floating')) return;
    const style = getComputedStyle(el);
    if (style.position !== 'fixed' && style.position !== 'sticky') return;
    const rect = el.getBoundingClientRect();
    if (rect.top > 2 || rect.height <= 0 || rect.height >= 200) return;
    if (rect.width < window.innerWidth * 0.6) return;
    offset = Math.max(offset, rect.bottom);
  });
  return Math.min(offset, 200);
}

// The horizontal extent of the publisher's own content column, so rails
// and corner items can stay in the gutters beside it instead of on top of
// it. Measured from the same real-paragraph detection native ads and
// in-content video anchor on; null (no constraint) when the page has no
// measurable paragraphs.
function contentColumnRect() {
  if (typeof document === 'undefined') return null;
  let left = Infinity;
  let right = -Infinity;
  for (const p of findArticleParagraphs(document.body)) {
    if (p.closest('.playbid-floating')) continue;
    const rect = p.getBoundingClientRect();
    if (rect.width <= 0) continue;
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  }
  return right > left ? { left, right } : null;
}

// One layout per page (same lifetime as BOOT_SCRIPT) - every floating
// widget registers with it so they're positioned against each other and the
// content column, not each in isolation. See floatingLayout.js.
let floatingLayoutState = null;
function floatingLayout() {
  floatingLayoutState ??= createFloatingLayout({
    getContentRect: contentColumnRect,
    getTopOffset: topHeaderOffset,
  });
  return floatingLayoutState;
}

function mountStickyBanner(unit, WidgetClass, bases) {
  const side = ['top', 'bottom', 'left', 'right'].includes(unit.placement?.side)
    ? unit.placement.side
    : 'bottom';
  const isSideRail = side === 'left' || side === 'right';
  const sizes = bannerSizeCandidates(unit, side);
  // Nothing this device can request for this side: no bar, no reserved space.
  if (!isDisplayDemandUnit(unit) || sizes.length === 0) return;
  let [width, height] = smallestSize(sizes);

  const bar = document.createElement('div');
  bar.id = unit.divId;
  bar.className = `playbid-floating playbid-sticky-banner playbid-sticky-banner--${side}`;
  Object.assign(bar.style, {
    position: 'fixed',
    zIndex: String(FLOATING_WIDGET_Z_INDEX),
    background: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  });
  if (isSideRail) {
    Object.assign(bar.style, { [side]: '8px', width: `${width}px` });
  } else {
    Object.assign(bar.style, {
      left: '0',
      right: '0',
      [side]: '0',
      display: 'flex',
      justifyContent: 'center',
    });
  }
  document.body.appendChild(bar);

  // DisplayWidget is constructed non-responsive below, so the creative
  // always renders at exactly [width, height]; floatingLayout shrinks it
  // (a transform, never a re-render) and reserves/positions from that
  // known size rather than re-measuring the DOM.
  const layout = floatingLayout();
  let unregister = isSideRail
    ? layout.addRail({ el: bar, side, width, height })
    : layout.addBar({ el: bar, side, width, height });

  const widget = new WidgetClass({
    clickTarget: '_blank',
    dismissible: Boolean(unit.dismissible),
    responsive: false,
  });
  if (typeof widget.on === 'function') {
    widget.on('creative:dismissed', () => {
      bar.remove();
      unregister();
    });
  }
  widget.initialize(bar);
  loadDisplay(widget, unit, bases, {
    elementId: `${unit.divId}-gpt`,
    sizes,
    box: [width, height],
    unitType: 'banner',
    onEmpty: () => {
      bar.remove();
      unregister();
      safeDestroy(widget);
    },
    onFilled: ({ size }) => {
      if (!Array.isArray(size)) return;
      [width, height] = size;
      // floatingLayout has no in-place resize - re-registering with the new
      // size is what makes it re-reserve page space to what GAM actually
      // served.
      unregister();
      unregister = isSideRail
        ? layout.addRail({ el: bar, side, width, height })
        : layout.addBar({ el: bar, side, width, height });
    },
  });
}

// `unit.delayMs` defers mounting the WHOLE bar (container, page-inset
// reservation, everything) - not just the creative inside an
// already-present container - matching ad-units.js's own
// `delay > 0 ? after(delay, show) : show()`. DisplayWidget's OWN delayMs is
// deliberately left at its default (0) for this call site so the delay
// isn't applied twice.
function renderStickyBannerUnit(unit, WidgetClass, bases) {
  const delayMs = Number.isFinite(unit.delayMs) && unit.delayMs > 0 ? unit.delayMs : 0;
  if (delayMs > 0) {
    setTimeout(() => mountStickyBanner(unit, WidgetClass, bases), delayMs);
  } else {
    mountStickyBanner(unit, WidgetClass, bases);
  }
}

// --- Corner slider: another self-injected floating widget, revealed on
// scroll instead of appearing immediately --------------------------------
//
// Ports playbid-sdk-plugin's own ad-units.js renderSlider() mechanics:
// fixed to a bottom corner (`placement.corner`), hidden (off-screen slide or
// transparent fade, per `placement.animation`) until the page has been
// scrolled `placement.scrollTriggerPct`% of its scrollable height, then
// reveals once and never re-hides. Like sticky banners, the SDK creates and
// injects this container itself - no publisher div required.
const CORNER_SLIDER_DEFAULT_SCROLL_TRIGGER_PCT = 30; // matches ad-units.js's own default

// Respects the same OS/browser-level accessibility preference ad-units.js
// already does - a user who's asked for less motion gets the ad appearing
// instantly at the scroll trigger, never a slide/fade animation.
function prefersReducedMotion() {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

function mountCornerSlider(unit, WidgetClass, bases) {
  const isLeft = unit.placement?.corner === 'bottom-left';
  const fade = unit.placement?.animation === 'fade';
  const sizes = deviceSizes(unit);
  if (!isDisplayDemandUnit(unit) || sizes.length === 0) return;
  let [width, height] = smallestSize(sizes);

  // Horizontal/vertical position, shrink-to-fit and visibility all come from
  // floatingLayout() - it keeps the slider clear of a visible rail on the
  // same side (staying on that side, as jobopting.com's own slider does),
  // above a bottom bar, stacked with any other corner item rather than on
  // top of it, and - on desktop - beside the article column instead of over
  // it.
  const box = document.createElement('div');
  box.id = unit.divId;
  box.className = 'playbid-floating playbid-corner-slider';
  Object.assign(box.style, {
    position: 'fixed',
    zIndex: String(FLOATING_WIDGET_Z_INDEX),
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
  });

  const motionReduced = prefersReducedMotion();
  if (!motionReduced) {
    box.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
    box.style.opacity = '0';
    box.style.transform = fade ? 'none' : `translateX(${isLeft ? '-120%' : '120%'})`;
  }
  document.body.appendChild(box);
  const layout = floatingLayout();
  let unregister = layout.addCorner({
    el: box,
    side: isLeft ? 'left' : 'right',
    width,
    height,
  });

  let revealed = false;
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    if (!motionReduced) {
      // A fade unit never touched `transform` on the way in (it's already
      // 'none'), so reveal shouldn't touch it either - only slide actually
      // uses transform as its hide/show mechanism.
      if (!fade) box.style.transform = 'translateX(0)';
      box.style.opacity = '1';
    }
    window.removeEventListener('scroll', onScroll);
  };
  const onScroll = () => {
    const scrolled = window.scrollY || document.documentElement.scrollTop;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight || 1;
    // `scrollTriggerPct: 0` is a valid "reveal immediately" - nullish
    // coalescing (not `||`) so it isn't swallowed by falsy-zero.
    const trigger = unit.placement?.scrollTriggerPct ?? CORNER_SLIDER_DEFAULT_SCROLL_TRIGGER_PCT;
    if ((scrolled / scrollable) * 100 >= trigger) reveal();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // handles trigger 0 and a page too short to ever fire a scroll event

  const widget = new WidgetClass({
    clickTarget: '_blank',
    dismissible: Boolean(unit.dismissible),
    responsive: false,
  });
  if (typeof widget.on === 'function') {
    widget.on('creative:dismissed', () => {
      window.removeEventListener('scroll', onScroll);
      box.remove();
      unregister();
    });
  }
  widget.initialize(box);
  loadDisplay(widget, unit, bases, {
    elementId: `${unit.divId}-gpt`,
    sizes,
    box: [width, height],
    unitType: 'corner_slider',
    onEmpty: () => {
      window.removeEventListener('scroll', onScroll);
      box.remove();
      unregister();
      safeDestroy(widget);
    },
    onFilled: ({ size }) => {
      if (!Array.isArray(size)) return;
      [width, height] = size;
      unregister();
      unregister = layout.addCorner({ el: box, side: isLeft ? 'left' : 'right', width, height });
    },
  });
}

// Same "defer the whole mount, not just the creative" semantics as
// renderStickyBannerUnit() - see its own comment for why.
function renderCornerSliderUnit(unit, WidgetClass, bases) {
  const delayMs = Number.isFinite(unit.delayMs) && unit.delayMs > 0 ? unit.delayMs : 0;
  if (delayMs > 0) {
    setTimeout(() => mountCornerSlider(unit, WidgetClass, bases), delayMs);
  } else {
    mountCornerSlider(unit, WidgetClass, bases);
  }
}

// --- Floating video: shared by two paths ----------------------------------
//
// `outstream_video` units carry `placement.mode`: `"in-content"` (default -
// plays inline in a self-injected container placed into the page's real
// content, see mountInContentVideo()/createInContentVideoContainer() below -
// and, ported from ad-units.js's renderVideo(), STICKS to this same floating
// position once scrolled past, unsticking on scroll-back) or `"sticky"`
// (always floating from the start - mountStickyVideo(), below, ported from
// ad-units.js's `u.placement === "sticky"` branch). Both self-inject their
// own container - no publisher-placed div for either - and both use the
// identical fixed bottom-right box (FLOATING_VIDEO_STYLE, positioned by
// floatingLayout() - which stacks it with a corner slider on the same
// corner instead of overlapping it) and close button
// (createFloatingCloseButton() - VideoWidget has no built-in `dismissible`
// the way DisplayWidget does, so this is built here and calls
// widget.destroy() directly).
//
// Both wire B10 too - see wireVideoBeacon() below, called from each mount
// function once the widget is constructed.

// Only the look - position/size come from floatingLayout().addCorner().
const FLOATING_VIDEO_STYLE = {
  position: 'fixed',
  zIndex: String(FLOATING_WIDGET_Z_INDEX),
  background: '#000',
  borderRadius: '8px',
  boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
};

function createFloatingCloseButton(onClick) {
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Close video advertisement');
  closeButton.textContent = '×';
  Object.assign(closeButton.style, {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '20px',
    height: '20px',
    lineHeight: '18px',
    padding: '0',
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    fontSize: '14px',
    fontFamily: 'sans-serif',
    cursor: 'pointer',
    zIndex: '1',
  });
  closeButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  });
  return closeButton;
}

// A docked player is a corner mini-player, not the in-content size: the
// desktop player (640x480) docked at full width covers a third of the page.
const DOCKED_VIDEO_MAX_WIDTH = 400;

function mountStickyVideo(unit, WidgetClass, bases) {
  if (!hasVideoDemand(unit)) return;
  const [rawWidth] = deviceSizes(unit)[0] ?? [320, 240];
  const width = Math.min(rawWidth, DOCKED_VIDEO_MAX_WIDTH);

  const box = document.createElement('div');
  box.id = unit.divId;
  box.className = 'playbid-floating playbid-sticky-video';
  Object.assign(box.style, FLOATING_VIDEO_STYLE);
  document.body.appendChild(box);
  // video.js runs `fluid`, so it sizes itself from the box's width - no
  // transform needed, and its height is measured rather than declared.
  const unregister = floatingLayout().addCorner({
    el: box,
    side: 'right',
    width,
    scaleContent: false,
  });

  const player = playOutstream(box, unit, WidgetClass, bases, {
    onEmpty: () => {
      unregister();
      box.remove();
    },
  });
  box.appendChild(
    createFloatingCloseButton(() => {
      try {
        player.destroy();
      } finally {
        box.remove();
        unregister();
      }
    }),
  );
}

// --- Outstream video: what plays inside a video container ----------------
//
// The publisher's own VAST tags (`demand: "vast"`) play directly. Otherwise a
// GAM-demand unit becomes one GPT slot on its video ad unit: GAM decides, and
// when a Prebid VIDEO bid wins, Prebid's renderer hands the bid to
// `renderVideo`, which plays its VAST in our own player (the slot's 1x1 Prebid
// creative is hidden). A display creative GAM serves instead (AdX, direct)
// stays in the slot as it is. Nothing to request -> the container collapses.

/** Whether a video unit has anything this page can play. */
function hasVideoDemand(unit) {
  return (unit?.demand !== 'gam' && Array.isArray(unit?.vastTags) && unit.vastTags.length > 0) || isGamUnit(unit);
}

function vastSourceOf(bid) {
  if (typeof bid?.vastUrl === 'string' && bid.vastUrl) return bid.vastUrl;
  if (typeof bid?.vastXml === 'string' && bid.vastXml) {
    return `data:application/xml;charset=utf-8,${encodeURIComponent(bid.vastXml)}`;
  }
  return null;
}

/**
 * Fill `host` with this unit's video. Returns `{ destroy }` for the mount's
 * close button; `onEmpty` runs when there turns out to be nothing to show.
 */
function playOutstream(host, unit, WidgetClass, bases, { onEmpty } = {}) {
  const options = {
    controls: true,
    fluid: true,
    muted: unit.playback?.muted !== false,
    autoplay: unit.playback?.autoplay !== false,
  };
  let widget = null;
  const control = { destroy: () => safeDestroy(widget) };

  if (unit.demand !== 'gam' && unit.vastTags?.length) {
    widget = new WidgetClass(options);
    wireVideoBeacon(host, unit, bases, widget);
    widget.initialize(host);
    widget.load({ vastUrl: unit.vastTags[0] });
    return control;
  }

  const sizes = deviceSizes(unit);
  if (!isGamUnit(unit) || sizes.length === 0) {
    onEmpty?.();
    return control;
  }
  const [w, h] = sizes[0];
  const slotEl = document.createElement('div');
  slotEl.id = `${unit.divId}-gpt`;
  slotEl.className = 'playbid-gam-slot';
  Object.assign(slotEl.style, { width: `${w}px`, maxWidth: '100%', minHeight: `${h}px`, margin: '0 auto' });
  host.appendChild(slotEl);

  const beacons = slotBeacons(unit, bases, 'outstream_video', { dwellMs: VIDEO_VIEWABILITY_DWELL_MS });
  bases.adServer.register({
    slot: slotOf(unit),
    elementId: slotEl.id,
    adUnitPath: unit.gam.adUnitPath,
    sizes,
    mediaType: 'video',
    video: unit.video,
    bidders: unit.bidders ?? [],
    floorCpm: unit.floorCpm,
    rendererUrl: bases.scriptBase ? `${bases.scriptBase}/playbid-video.umd.js` : undefined,
    renderVideo: (bid) => {
      const src = vastSourceOf(bid);
      if (!src || widget) return;
      // The player reports this ad from here on (start, quartiles, impression).
      beacons.handOff();
      slotEl.style.display = 'none';
      const playerHost = document.createElement('div');
      playerHost.className = 'playbid-video-player';
      host.appendChild(playerHost);
      widget = new WidgetClass(options);
      wireVideoBeacon(host, unit, bases, widget, { winningBid: () => bid });
      widget.initialize(playerHost);
      widget.load({ vastUrl: src });
    },
    onBidWon: (bid) => beacons.won(bid),
    onRender: (r) => {
      if (r.isEmpty) {
        onEmpty?.(r.reason);
        return;
      }
      slotEl.style.minHeight = '';
      beacons.filled(slotEl);
    },
  });
  return control;
}

function renderStickyVideoUnit(unit, WidgetClass, bases) {
  const delayMs = Number.isFinite(unit.delayMs) && unit.delayMs > 0 ? unit.delayMs : 0;
  if (delayMs > 0) {
    setTimeout(() => mountStickyVideo(unit, WidgetClass, bases), delayMs);
  } else {
    mountStickyVideo(unit, WidgetClass, bases);
  }
}

// --- In-content video: self-injected into the page's real content, then
// sticks to the corner once scrolled past -------------------------------
//
// Ported from ad-units.js's renderVideo(), but - unlike ad-units.js, which
// expects a publisher-placed divId here - this SDK self-injects the
// container too, the same "for every widget we have to inject them only"
// rule sticky banner/corner slider/sticky video already follow. The video
// plays inline (in a container this file creates and inserts into the
// page's real content - see createInContentVideoContainer()) until that
// container scrolls ABOVE the viewport (the reader has scrolled past it,
// not just resized past it) - at that point it "sticks" to the same
// floating bottom-right position (floatingLayout()) an always-sticky
// unit, gains a close button, and the original spot reserves its own height
// so the page doesn't jump. Scrolling back until the original spot
// re-enters the viewport "unsticks" it - back to normal in-place flow,
// close button removed. The same <video> element persists through both
// transitions (video.js is never re-initialized), so playback is never
// interrupted.
//
// The container itself is never repositioned - an inner wrapper this
// function creates and owns is what toggles between normal and fixed, the
// same "never touch the container element directly, wrap instead" rule
// DisplayRenderer already follows for a publisher-placed container.
//
// Anchors after the first real paragraph found in <body> (reusing the exact
// same paragraph-detection findArticleParagraphs() uses for native ads,
// below) - the common "ad after the first paragraph" placement convention -
// falling back to the end of <body> when the page has no real paragraph
// content, rather than never rendering at all.
function createInContentVideoContainer(unit) {
  const container = document.createElement('div');
  container.id = unit.divId;
  container.className = 'playbid-video-slot';
  const anchor = typeof document !== 'undefined' ? findArticleParagraphs(document.body)[0] : null;
  if (anchor) {
    anchor.parentNode.insertBefore(container, anchor.nextSibling);
  } else {
    document.body.appendChild(container);
  }
  return container;
}

function mountInContentVideo(unit, WidgetClass, bases) {
  if (!hasVideoDemand(unit)) return;
  const container = createInContentVideoContainer(unit);
  const wrapper = document.createElement('div');
  wrapper.className = 'playbid-video-wrap';
  container.appendChild(wrapper);

  let observer = null;
  // Watches `wrapper`, not `container` - the wrapper is the element actually
  // rendered to the user (in-place or, later, floating), the same reason
  // stick()/unstick() toggle its styles rather than the container's own.
  // One requestId for the unit's whole lifecycle - stick/unstick never
  // re-wires this.
  const player = playOutstream(wrapper, unit, WidgetClass, bases, {
    onEmpty: () => {
      observer?.disconnect();
      container.remove();
    },
  });

  if (typeof IntersectionObserver === 'undefined') return;

  const [rawWidth] = deviceSizes(unit)[0] ?? [320, 240];
  const width = Math.min(rawWidth, DOCKED_VIDEO_MAX_WIDTH);
  let stuck = false;
  let closeButton = null;
  let unregister = null;

  const stick = () => {
    if (stuck) return;
    stuck = true;
    container.style.minHeight = `${wrapper.offsetHeight}px`;
    wrapper.classList.add('playbid-floating');
    Object.assign(wrapper.style, FLOATING_VIDEO_STYLE);
    unregister = floatingLayout().addCorner({
      el: wrapper,
      side: 'right',
      width,
      scaleContent: false,
    });
    closeButton = createFloatingCloseButton(() => {
      observer.disconnect();
      try {
        player.destroy();
      } finally {
        container.style.minHeight = '';
        wrapper.remove();
        unregister?.();
        unregister = null;
      }
    });
    wrapper.appendChild(closeButton);
  };
  const unstick = () => {
    if (!stuck) return;
    stuck = false;
    container.style.minHeight = '';
    unregister?.();
    unregister = null;
    wrapper.classList.remove('playbid-floating');
    Object.assign(wrapper.style, {
      position: '',
      left: '',
      right: '',
      bottom: '',
      width: '',
      display: '',
      zIndex: '',
      background: '',
      borderRadius: '',
      boxShadow: '',
    });
    closeButton?.remove();
    closeButton = null;
  };

  observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting && entry.boundingClientRect.bottom <= 0) stick();
      else if (entry.isIntersecting) unstick();
    },
    { threshold: 0 },
  );
  observer.observe(container);
}

function renderBannerUnit(unit, container, WidgetClass, bases) {
  const sizes = deviceSizes(unit);
  if (!isDisplayDemandUnit(unit) || sizes.length === 0) return;
  const mount = () => {
    // `unit.dismissible` rides straight through from the boot response -
    // DisplayWidget owns the close button. `delayMs` defers the whole mount
    // here rather than inside the widget: a GAM slot must exist in the DOM
    // before GAM answers, or GPT has nowhere to paint the winner.
    const widget = new WidgetClass({
      clickTarget: '_blank',
      dismissible: Boolean(unit.dismissible),
    });
    widget.initialize(container);
    loadDisplay(widget, unit, bases, {
      elementId: `${unit.divId}-gpt`,
      sizes,
      box: smallestSize(sizes),
      unitType: 'banner',
      onEmpty: () => safeDestroy(widget),
    });
  };
  const delayMs = Number.isFinite(unit.delayMs) && unit.delayMs > 0 ? unit.delayMs : 0;
  if (delayMs > 0) setTimeout(mount, delayMs);
  else mount();
}

// In-article native ads (Atlas: `placement.mode === "in-content"`).
// `unit.divId` is NOT a single ad slot here - it's the ARTICLE WRAPPER the
// publisher marks (same explicit-divId convention as every other unit, just
// a container to search rather than a container to fill directly). This
// port's placement math matches playbid-sdk-plugin's own already-shipped
// ad-units.js renderNative() exactly, for consistency across both products:
// `spacing = placement.minParagraphSpacing` paragraphs between ads,
// `count = placement.count` ads requested (both clamped/defaulted below),
// ad `n` (1-indexed) is inserted immediately after paragraph `n * spacing`.
// A short article that can't fit `count` ads at that spacing renders fewer,
// never crowds them closer together than `spacing` allows.
const DEFAULT_NATIVE_COUNT = 2;
const DEFAULT_NATIVE_SPACING = 3;

// A "paragraph" worth anchoring an ad after: real body text, not a caption/
// fragment (matches ad-units.js's own 40-character floor), and not a <p>
// that itself wraps another <p> (some CMSes nest these; the outer one would
// double-count).
//
// Never a <p> inside something the SDK itself injected: a mounted
// video.js player carries its own long `<p class="vjs-modal-dialog-description">`
// inside a hidden modal, and on a page whose units mount the in-content
// video first, that <p> used to become "paragraph 2" - so the read-aloud
// card (and its ask box) was injected inside a `display: none` dialog.
const SDK_OWNED_CONTENT =
  '.playbid-floating, .playbid-video-slot, .playbid-readaloud-slot, .playbid-native-slot, ' +
  '[data-playbid-unit], .video-js';

function findArticleParagraphs(articleRoot) {
  const paragraphs = [];
  articleRoot.querySelectorAll('p').forEach((p) => {
    if ((p.textContent ?? '').trim().length < 40) return;
    if (p.querySelector('p')) return;
    if (p.closest(SDK_OWNED_CONTENT)) return;
    paragraphs.push(p);
  });
  return paragraphs;
}

function renderNativeUnit(unit, articleRoot, WidgetClass, bases) {
  const paragraphs = findArticleParagraphs(articleRoot);
  if (!paragraphs.length) return;
  if (!isDisplayDemandUnit(unit)) return;

  const spacing =
    unit.placement?.minParagraphSpacing > 0
      ? unit.placement.minParagraphSpacing
      : DEFAULT_NATIVE_SPACING;
  const requestedCount = unit.placement?.count > 0 ? unit.placement.count : DEFAULT_NATIVE_COUNT;
  const slotCount = Math.min(requestedCount, Math.max(1, Math.floor(paragraphs.length / spacing)));

  // Only sizes that fit the article column: a 728x90 in a 360px phone column
  // would overflow the text it sits between.
  const columnWidth = articleRoot.clientWidth || window.innerWidth || 0;
  const sizes = deviceSizes(unit).filter(([w]) => !columnWidth || w <= columnWidth);
  if (!sizes.length) return;

  for (let n = 1; n <= slotCount; n += 1) {
    const anchor = paragraphs[n * spacing];
    if (!anchor) break;

    const slot = document.createElement('div');
    slot.className = 'playbid-native-slot';
    slot.style.margin = '18px 0';
    anchor.parentNode.insertBefore(slot, anchor.nextSibling);

    // Reuses DisplayWidget exactly as the banner path does - just placed
    // between paragraphs instead of into a single publisher-given div. Each
    // position is its own widget instance and its own GPT slot on the same
    // GAM ad unit, so a click/error/viewability event on one in-article ad
    // never affects the others.
    const widget = new WidgetClass({ clickTarget: '_blank' });
    widget.initialize(slot);
    loadDisplay(widget, unit, bases, {
      elementId: `${unit.divId}-gpt-${n}`,
      sizes,
      box: smallestSize(sizes),
      unitType: 'native',
      onEmpty: () => {
        safeDestroy(widget);
        slot.remove();
      },
    });
  }
}

// --- Read-aloud ("Listen & Explore"): self-injected into article content -
//
// An `audio` format unit never renders a standalone player - its
// `positions`/`vastTags` become ReadAloudWidget's ad breaks (pre/mid/post),
// and the widget itself is a self-injected "Listen & Explore" card, the same
// self-injection convention every other floating/in-content widget in this
// file already follows (no publisher-placed `<div id="unit.divId">` is
// required or looked up). Reuses findArticleParagraphs() - the exact same
// real-paragraph heuristic (>= 40 characters, no nested <p>) renderNativeUnit()
// already uses - so both the ANCHOR (where to inject) and the CONTENT (what
// to read) come from one tested source, scanning the whole page
// (`document.body`) rather than a publisher-marked wrapper, matching
// createInContentVideoContainer()'s own `document.body` scan for the same
// reason: there is no publisher-authored article-wrapper convention for this
// widget to rely on.
//
// Injects after the SECOND real paragraph found (matches the reference
// implementation's own `after(paras[1], ...)` placement exactly), falling
// back to the first paragraph on a short article, and doing nothing at all
// (never throws, never renders a broken/empty card) when the page has no
// real paragraph content to read.
//
// TODO (deferred, not yet scheduled): on a real job-listing page this
// silently renders nothing when the content is structured as bullet lists
// (requirements/responsibilities in <li>, no real <p> tags) rather than
// prose paragraphs - confirmed live, and confirmed to be exactly why the
// reference implementation (jobopting.com/playbid-demo/units.js) does NOT
// reuse its own `paras` (p-only) list for reading content. It keeps two
// separate lists: `paras` (p-only, for ANCHOR placement only) and a broader
// `readEls` - `post.querySelectorAll(':scope > h1, :scope > h2, :scope > p,
// :scope > ul > li')` - for the actual per-paragraph reading content, with
// no 40-char filter, just "non-empty after normalization". The fix here is
// a NEW, read-aloud-specific content collector (not a change to the shared
// findArticleParagraphs(), which native-ad/video placement also depend on
// and which is already tested/verified against live behavior) that mirrors
// `readEls`: include <li> (under a direct <ul>) and headings, scanned from
// `document.body` (no `.post`-equivalent wrapper exists for this SDK's
// zero-config self-injection). Keep the existing p-only list as the ANCHOR
// point, falling back to the richer list's first element only when zero
// <p> tags exist at all.
//
// `chat_sponsored` (ADR-031) is the same widget's ask box, not a widget of
// its own: when the site has a `chat_sponsored` unit for this device, the ask
// box answers through `POST /v1/chat` (and may show a sponsored answer
// card); without one it answers from the article locally and never calls
// the chat endpoint - the reference implementation's `CHAT ? ... :
// fromArticle(q)`. There is ONE card per page: an `audio` unit mounts it
// with ad breaks and chat as configured; a `chat_sponsored` unit with no
// `audio` unit mounts it on its own, with no ad breaks.
function unitsFor(bases, format) {
  return (bases.units ?? []).filter((u) => u?.format === format && deviceAllowed(u));
}

function mountReadAloud(unit, WidgetClass, bases) {
  const paragraphs = findArticleParagraphs(document.body);
  if (!paragraphs.length) return;

  // The dashboard's Listen & Explore settings (sdk-config `listenExplore`):
  // either half can be off, and with both off - or this device excluded -
  // there is no card. Absent block = the card as before (both halves on).
  const listenExplore = bases.listenExplore;
  if (listenExplore && !deviceAllowed(listenExplore)) return;
  const readAloudEnabled = listenExplore?.readAloud?.enabled !== false;
  const exploreEnabled = listenExplore?.explore?.enabled !== false;
  if (!readAloudEnabled && !exploreEnabled) return;

  const chatUnit = unitsFor(bases, 'chat_sponsored')[0] ?? null;
  const isAudioUnit = unit.format === 'audio';
  const position = listenExplore?.explore?.position;

  const container = document.createElement('div');
  if (unit.divId) container.id = unit.divId;
  container.className = 'playbid-readaloud-slot';
  // 'top': above the first real paragraph, i.e. after the title and byline;
  // 'floating': out of the article (the card pins itself bottom-right);
  // otherwise after the second paragraph.
  if (position === 'floating') {
    document.body.appendChild(container);
  } else if (position === 'top') {
    paragraphs[0].parentNode.insertBefore(container, paragraphs[0]);
  } else {
    const anchor = paragraphs[1] ?? paragraphs[0];
    anchor.parentNode.insertBefore(container, anchor.nextSibling);
  }

  const widget = new WidgetClass({
    apiKey: bases.siteKey,
    apiBase: bases.configOrigin,
    articleUrl: window.location.href,
    chatEnabled: chatUnit !== null,
    layout: listenExplore?.explore?.layout,
    position: position === 'top' || position === 'floating' ? position : undefined,
    readAloudEnabled,
    exploreEnabled,
    suggestions: listenExplore?.explore?.suggestions,
  });
  if (chatUnit) wireChatSponsoredBeacon(widget, chatUnit, bases);
  widget.initialize(container);
  widget.load({
    paragraphs: paragraphs.map((el) => ({
      text: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
      el,
    })),
    // Breaks only with real VAST tags: no placeholder ad is ever read out.
    adBreaks:
      isAudioUnit && unit.vastTags?.length
        ? {
            vastTags: unit.vastTags,
            positions: {
              pre: unit.positions?.preRoll === true,
              mid: unit.positions?.midRoll === true,
              post: unit.positions?.postRoll === true,
            },
          }
        : undefined,
  });
}

// A `chat_sponsored` unit only mounts the card when no `audio` unit will -
// otherwise the audio unit's card already carries the chat.
function mountChatSponsored(unit, WidgetClass, bases) {
  if (unitsFor(bases, 'audio').length) return;
  if (unitsFor(bases, 'chat_sponsored')[0] !== unit) return;
  mountReadAloud(unit, WidgetClass, bases);
}

// B10 for a sponsored chat answer: one `impression` per sponsored card that
// becomes MRC-viewable, its own requestId each. Never a click beacon - the
// card links to a signed /v1/c/ redirect (payload `u`/`s: "chat_sponsored"`)
// that counts the click itself.
function wireChatSponsoredBeacon(widget, unit, { configOrigin, siteKey }) {
  if (typeof crypto === 'undefined' || !crypto.randomUUID) return;
  if (typeof widget?.on !== 'function') return;
  const pageUrl = `${window.location.origin}${window.location.pathname}`;
  widget.on('chat:sponsoredImpression', () =>
    fireBeacon(configOrigin, {
      apiKey: siteKey,
      requestId: crypto.randomUUID(),
      event: 'impression',
      unitType: unit.format,
      slot: unit.key ?? unit.format,
      pageUrl,
    }),
  );
}

// --- Contextual ads: in-text links, commerce cards, related searches -----
// (ADR-030, ADR-031)
//
// All match ads to the article's own text, so all need the article: the
// nearest element containing every real paragraph (findArticleParagraphs(),
// the same heuristic native ads and read-aloud use), ignoring paragraphs in
// page chrome - a long footer disclaimer must not widen the "article" to
// the whole <body>. Nothing renders on a page with no real paragraphs.
const CHROME_ANCESTORS = 'nav, header, footer, aside, form';

function findArticleRoot() {
  const paragraphs = findArticleParagraphs(document.body).filter(
    (p) => !p.closest(CHROME_ANCESTORS),
  );
  if (!paragraphs.length) return null;
  let root = paragraphs[0].parentElement;
  while (root && !paragraphs.every((p) => root.contains(p))) {
    root = root.parentElement;
  }
  return { root: root ?? document.body, paragraphs };
}

// B10 for contextual units: an `impression` beacon each time the widget
// reports one (per link for in-text, once per block for commerce and related
// searches - each
// with its own requestId, as the reference implementation sends them).
// Deliberately NO click beacon: every link/card points at a signed
// `/v1/c/...` redirect that records the click itself, so a beacon here would
// count every click twice. And no `viewable` either - the reference sends
// only `impression` for these unit types.
//
// `unitType` is the raw format (`in_text`/`contextual_commerce`) and `slot`
// is the unit key - both verified against the live click redirects, whose
// signed payloads carry `u: "in_text"` / `s: "in_text"`
// (`u: "contextual_commerce"` / `s: "commerce"` for commerce, `u`/`s: "rsoc"`
// for related searches).
function wireContextualBeacon(widget, unit, { configOrigin, siteKey }) {
  if (typeof crypto === 'undefined' || !crypto.randomUUID) return;
  if (typeof widget?.on !== 'function') return;
  const pageUrl = `${window.location.origin}${window.location.pathname}`;
  widget.on('ad:impression', () =>
    fireBeacon(configOrigin, {
      apiKey: siteKey,
      requestId: crypto.randomUUID(),
      event: 'impression',
      unitType: unit.format,
      slot: unit.key ?? unit.format,
      pageUrl,
    }),
  );
}

// The dashboard's own settings ride through only when they are valid, so
// one malformed field can never stop an otherwise-good unit rendering (the
// widget's config validation would otherwise reject it outright).
function positiveIntegerOrUndefined(value) {
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

// In-text: the widget is initialized on the article itself - its links go
// into the article's own paragraphs, so there is no container to inject.
function mountInText(unit, WidgetClass, bases) {
  const article = findArticleRoot();
  if (!article) return;

  const settings = unit.inText ?? {};
  const widget = new WidgetClass({
    apiKey: bases.siteKey,
    apiBase: bases.configOrigin,
    maxLinks: positiveIntegerOrUndefined(settings.maxLinks),
    linkStyle: Object.values(INTEXT_LINK_STYLES).includes(settings.linkStyle)
      ? settings.linkStyle
      : undefined,
  });
  wireContextualBeacon(widget, unit, bases);
  widget.initialize(article.root);
  widget.load();
}

// Commerce: a self-injected block after the fifth real paragraph (the
// reference implementation's `paras[min(4, n - 1)]`), or the last one on a
// shorter article. The widget never renders into an empty block: on a
// no-fill or an error it removes nothing it did not add, and the slot itself
// is removed here so no empty margin is left behind.
const COMMERCE_ANCHOR_PARAGRAPH = 4;

function mountCommerce(unit, WidgetClass, bases) {
  const article = findArticleRoot();
  if (!article) return;

  const { paragraphs } = article;
  const anchor = paragraphs[Math.min(COMMERCE_ANCHOR_PARAGRAPH, paragraphs.length - 1)];
  const slot = document.createElement('div');
  if (unit.divId) slot.id = unit.divId;
  slot.className = 'playbid-commerce-slot';
  slot.setAttribute('data-playbid-unit', unit.key ?? unit.format);
  anchor.parentNode.insertBefore(slot, anchor.nextSibling);

  const widget = new WidgetClass({
    apiKey: bases.siteKey,
    apiBase: bases.configOrigin,
    maxCards: positiveIntegerOrUndefined(unit.commerce?.maxCards),
  });
  wireContextualBeacon(widget, unit, bases);
  const removeSlot = () => slot.remove();
  widget.on('ad:empty', removeSlot);
  widget.on('ad:error', removeSlot);
  widget.initialize(slot);
  widget.load({ articleRoot: article.root });
}

// Related searches: a self-injected "Related searches · Sponsored" box at
// the dashboard's `rsoc.position`, ported from the reference implementation:
// `after-intro` (default) after the first real paragraph, `in-content` after
// the fourth (`paras[min(3, n - 1)]`), `below-article` after the article's
// last element. `rsoc.count`/`rsoc.style` pass through when valid.
// `rsoc.termsSource` (`"auto"` is the only value seen live) is the backend's
// concern and is not read here. Like commerce, the slot is removed again on
// a no-fill or an error.
const RSOC_IN_CONTENT_PARAGRAPH = 3;

function rsocAnchor(article, position) {
  const { root, paragraphs } = article;
  if (position === 'below-article') return root.lastElementChild ?? paragraphs.at(-1);
  if (position === 'in-content') {
    return paragraphs[Math.min(RSOC_IN_CONTENT_PARAGRAPH, paragraphs.length - 1)];
  }
  return paragraphs[0];
}

function mountRelatedSearch(unit, WidgetClass, bases) {
  const article = findArticleRoot();
  if (!article) return;

  const settings = unit.rsoc ?? {};
  const anchor = rsocAnchor(article, settings.position);
  const slot = document.createElement('div');
  if (unit.divId) slot.id = unit.divId;
  slot.className = 'playbid-rsoc-slot';
  slot.setAttribute('data-playbid-unit', unit.key ?? unit.format);
  anchor.parentNode.insertBefore(slot, anchor.nextSibling);

  const widget = new WidgetClass({
    apiKey: bases.siteKey,
    apiBase: bases.configOrigin,
    count: positiveIntegerOrUndefined(settings.count),
    style: Object.values(RSOC_STYLES).includes(settings.style) ? settings.style : undefined,
  });
  wireContextualBeacon(widget, unit, bases);
  const removeSlot = () => slot.remove();
  widget.on('ad:empty', removeSlot);
  widget.on('ad:error', removeSlot);
  widget.initialize(slot);
  widget.load({ articleRoot: article.root });
}

// Renders one unit into its own `divId` element. Never throws - a missing
// container (publisher hasn't added that div yet), a missing widget class,
// or a widget construction/load error all resolve quietly with a
// console.warn at most, matching every other "never throw into the host
// page" boundary in this file.
function renderUnit(unit, type, bases) {
  // Checked before anything else, so it applies uniformly to every path
  // below (sticky banners included, which never reach the generic divId
  // lookup) - a unit this device isn't targeted for is never constructed,
  // never touches the DOM, and never wires a beacon.
  if (!deviceAllowed(unit)) return;

  const WidgetClass = window.PlaybidSDK?.[WIDGET_TYPE_TO_GLOBAL[type]];
  if (typeof WidgetClass !== 'function') return;

  // Sticky banners, the corner slider, both video placements, and read-aloud
  // are all self-contained - the SDK creates and injects their own container
  // (see mountStickyBanner()/mountCornerSlider()/mountInContentVideo()/
  // mountStickyVideo()/mountReadAloud()), so there's no publisher-placed
  // element to look up first. A non-sticky `banner` uses `unit.divId` when
  // the page has it and otherwise lays its own box into the page flow (see
  // inFlowBannerContainer()); `native` still expects the article wrapper.
  if (type === 'inText' || type === 'commerce' || type === 'relatedSearch') {
    try {
      if (type === 'inText') mountInText(unit, WidgetClass, bases);
      else if (type === 'commerce') mountCommerce(unit, WidgetClass, bases);
      else mountRelatedSearch(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }
  if (type === 'readAloud') {
    try {
      if (unit?.format === 'chat_sponsored') mountChatSponsored(unit, WidgetClass, bases);
      else mountReadAloud(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }
  if (type === 'display' && unit?.format === 'banner' && unit?.placement?.sticky === true) {
    try {
      renderStickyBannerUnit(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }
  if (type === 'display' && unit?.format === 'corner_slider') {
    try {
      renderCornerSliderUnit(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }
  if (type === 'video' && unit?.placement?.mode === 'sticky') {
    try {
      renderStickyVideoUnit(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }
  if (type === 'video') {
    // In-content video is self-injected too, same as every other floating
    // unit above - see createInContentVideoContainer(). No publisher-placed
    // `<div id="unit.divId">` is required or looked up; `unit.playback.muted`/
    // `.autoplay` ride straight through from the boot response, `!== false`
    // (true unless explicitly turned off) - matches ad-units.js's own
    // default-on convention for both. Also sticks to the corner once
    // scrolled past and unsticks on scroll-back - see mountInContentVideo().
    try {
      mountInContentVideo(unit, WidgetClass, bases);
    } catch (error) {
      console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
    }
    return;
  }

  const container =
    typeof document !== 'undefined'
      ? (document.getElementById(unit?.divId) ?? inFlowBannerContainer(unit, type))
      : null;
  if (!container) return;

  try {
    if (type === 'display') {
      if (unit.format === 'native') {
        renderNativeUnit(unit, container, WidgetClass, bases);
      } else {
        renderBannerUnit(unit, container, WidgetClass, bases);
      }
    }
  } catch (error) {
    console.warn(`[PlaybidSDK] boot(): failed to render unit "${unit?.key}"`, error);
  }
}

// A non-sticky top or bottom banner whose `<div id="unit.divId">` is not on
// the page. The dashboard offers "in the page flow" without asking the
// publisher to place a div, so before this the position rendered nothing on
// every page that had not added one by hand. The SDK now lays the box into the
// page flow itself: the first child of <body> for `top`, the last for
// `bottom`, centred. A publisher-placed div with that id still wins, so a
// page can put the banner exactly where it wants. Rails are always sticky and
// never reach here; `native` keeps needing its article wrapper.
function inFlowBannerContainer(unit, type) {
  if (type !== 'display' || unit?.format !== 'banner' || !unit?.divId) return null;
  const side = unit?.placement?.side;
  if (side !== 'top' && side !== 'bottom') return null;
  const body = document.body;
  if (!body) return null;
  const el = document.createElement('div');
  el.id = unit.divId;
  el.className = `playbid-inflow-banner playbid-inflow-banner--${side}`;
  el.style.display = 'flex';
  el.style.justifyContent = 'center';
  el.style.margin = '8px auto';
  if (side === 'top') body.insertBefore(el, body.firstChild);
  else body.appendChild(el);
  return el;
}

// Injects one <script> per needed widget type and resolves once every one of
// them has settled - on `load` AND on `error` (never rejects), matching the
// "never throw into the host page" convention TrackingDispatcher and
// requestWidgetConfig() already follow elsewhere in this codebase.
function loadWidgetScript(scriptBase, type) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `${scriptBase}/playbid-${type}.umd.js`;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      console.warn(`[PlaybidSDK] boot(): failed to load widget script for "${type}"`);
      resolve();
    });
    (document.head ?? document.body).appendChild(script);
  });
}

// Only `video` actually has a stylesheet to load - VideoJSAdapter.js is the
// only file anywhere in `src/` that imports one (video.js's own CSS).
// Real bug this closes: a page loading only playbid-boot.umd.js (the whole
// point of per-widget CDN loading) never touched playbid-sdk.css either, so
// video.js rendered completely unstyled - the ARIA/control-bar text video.js
// normally hides via CSS sprawled across the page as plain visible text, and
// `fluid` sizing (a CSS-driven aspect-ratio trick) broke, letting the player
// grow to an arbitrary size. `playbid-video.css` (built by vite.config.js's
// `cssFileName`) is what this loads. Types with nothing to style resolve
// immediately without fetching anything - never a needless 404.
const TYPES_WITH_CSS = new Set(['video']);
function loadWidgetStyles(scriptBase, type) {
  if (!TYPES_WITH_CSS.has(type)) return Promise.resolve();
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${scriptBase}/playbid-${type}.css`;
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () => {
      console.warn(`[PlaybidSDK] boot(): failed to load styles for "${type}"`);
      resolve();
    });
    (document.head ?? document.body).appendChild(link);
  });
}

// B1: GET /v1/sdk-config/{siteKey}. Never throws - a failure here means
// boot() itself resolves to null, since widget loading depends on it.
//
// Stale-config fallback (Atlas ch.4 B2): every good answer is kept in this
// origin's localStorage, and when the call fails the page boots on the last
// one for the SAME site key. An API outage then degrades to yesterday's
// config, never to zero revenue. Storage that is blocked or full is simply
// skipped.
function configStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

async function fetchSdkConfig(configOrigin, siteKey) {
  try {
    const response = await fetch(`${configOrigin}/v1/sdk-config/${encodeURIComponent(siteKey)}`);
    if (!response.ok) throw new Error(`unexpected status ${response.status}`);
    const config = await response.json();
    if (!config || typeof config !== 'object') throw new Error('sdk-config malformed');
    writeCachedConfig(configStorage(), config);
    return config;
  } catch (error) {
    const cached = readCachedConfig(configStorage());
    if (cached?.config?.siteKey === siteKey) {
      console.warn('[PlaybidSDK] boot(): sdk-config unavailable, booting on the last good config', error);
      return cached.config;
    }
    throw error;
  }
}

// B2's bearer is what the widgets' telemetry client (src/api/) reports with:
// heartbeat, widget lifecycle, playback and errors to /api/v1/*. boot() never
// imports that client - it stays in the widget bundles - so it leaves the
// settings on the page for the first widget that asks for one (see
// BackendConnection.js PENDING_KEY). Advertisement events are OFF there: the
// B10 beacons above already report every impression, start, quartile and
// click, and the backend adds both streams into the same totals. A page that
// configured its own backend connection is left alone.
const TELEMETRY_PENDING_KEY = '__playbidBackendConfig';
const TELEMETRY_CONNECTION_KEY = '__playbidBackendConnection';
function shareTelemetrySettings(configOrigin, siteKey, telemetryAuth) {
  if (typeof window === 'undefined' || !telemetryAuth?.bearerToken) return;
  if (window[TELEMETRY_CONNECTION_KEY] || window[TELEMETRY_PENDING_KEY]) return;
  window[TELEMETRY_PENDING_KEY] = {
    baseUrl: configOrigin,
    apiKey: siteKey,
    telemetry: { adEvents: false },
    initialAuth: telemetryAuth,
  };
}

// B2: GET /api/v1/config, header-authenticated with the same siteKey B1 uses
// (both are the publisher's public site key, confirmed by the wire contract
// - B1's own response even echoes it back as `siteKey`). Mints a short-lived
// telemetry bearer plus volume/cost levers (flushIntervalMs, batchMaxEvents,
// heartbeatIntervalMs, samplingRate) for a future telemetry-sending phase to
// consume - boot() only fetches and exposes this, it does not act on it yet.
//
// Fired in parallel with B1 ("fired together" - Atlas ch.4 B1+B2), and fails
// INDEPENDENTLY of it: a broken telemetry bootstrap must never stop widget
// loading, matching the "never throw into the host page" convention
// everywhere else in this codebase. Returns null (with a console.warn) on
// any failure, never throws.
async function fetchTelemetryAuth(configOrigin, siteKey) {
  try {
    const response = await fetch(`${configOrigin}/api/v1/config`, {
      headers: { 'X-Publisher-Key': siteKey },
    });
    if (!response.ok) throw new Error(`unexpected status ${response.status}`);
    return await response.json();
  } catch (error) {
    console.warn('[PlaybidSDK] boot(): failed to fetch telemetry auth (api/v1/config)', error);
    return null;
  }
}

/**
 * Fetches the boot config for `siteKey` and loads only the widget bundle(s)
 * the response's `units[]` actually call for - never the whole SDK. Also
 * loads that widget's own CSS, if it has one (see loadWidgetStyles() -
 * `video` is the only one today) - a real bug this closes: a page loading
 * only the boot bundle previously got video.js with no stylesheet at all.
 *
 * @param {object} [options]
 * @param {string} options.siteKey     The publisher's public site key.
 * @param {string} [options.apiBase]   Explicit base for the config fetch
 *   (`GET {apiBase}/v1/sdk-config/{siteKey}`) only. Falls back to `cdnBase`,
 *   then to the bare origin of the currently-executing <script> tag.
 * @param {string} [options.cdnBase]   Explicit base for widget-script
 *   loading, and (if `apiBase` isn't given) the config fetch too. Falls back
 *   to the directory containing the currently-executing <script> tag.
 *   At least one of `apiBase`/`cdnBase` is required when boot() is called
 *   from a `<script type="module">` context, since `document.currentScript`
 *   is unavailable there by spec.
 * @returns {Promise<object|null>} The parsed B1 (sdk-config) response, with
 *   one additional key - `telemetryAuth` - holding B2's (api/v1/config)
 *   response verbatim, or `null` if B2 failed (widget loading is unaffected
 *   either way). Every other field is B1's own response untouched - nothing
 *   here renames or merges either contract's fields. Returns `null` overall
 *   if a base couldn't be resolved or B1 itself failed. Never rejects.
 *
 * As a side effect, also RENDERS: once the needed widget script(s) have
 * loaded, every unit in the response whose `format` maps to a widget is
 * constructed and loaded - with no publisher code beyond the <script> tag
 * that called this. `banner` (non-sticky) and `native` units are initialized
 * into `document.getElementById(unit.divId)` and are silently skipped if
 * that element isn't on the page yet (safe to call before it exists only if
 * boot() runs after it - same ordering requirement any DOM-querying code
 * has). Every other unit type is self-injected - the SDK creates and
 * inserts its own container - so there's nothing for the publisher to place
 * on the page at all for those: sticky banner (mountStickyBanner()), corner
 * slider (mountCornerSlider()), both outstream_video placement modes,
 * in-content included (mountInContentVideo()/mountStickyVideo()), and
 * read-aloud (mountReadAloud() - see its own section below).
 *
 * GAM-served units (`banner`, `native`, `corner_slider`, `outstream_video`
 * with `demand: "gam"`): each placement becomes one GPT slot on
 * `unit.gam.adUnitPath`, requesting only the sizes this device may show
 * (`sizesByDevice`). Every slot mounted in the same tick runs as ONE Prebid
 * auction (`unit.bidders`, priced on `config.auction`'s ladders) and ONE
 * batched GAM request - see ../headerbidding/PageAdServer.js. GPT and Prebid
 * load only when such a unit exists; the publisher's own copies are reused.
 * A slot GAM leaves empty collapses its container (a sticky banner releases
 * its reserved page space). A filled `banner`/`corner_slider` GAM slot with
 * `unit.refreshMs` refreshes per Atlas ch.8 (ADR-033, loadGamDisplay()): a new
 * auction for that slot only after `refreshMs` (floor 30 s) of in-view time,
 * each refreshed fill beaconed under its own requestId. Display units with `demand: "adsense"` mount
 * exactly the same way but fill with one `<ins class="adsbygoogle">` at this
 * device's reserved size (`unit.adsense.client`/`.slot`): `adsbygoogle.js`
 * loads once per page (never when the page already has it), each `<ins>` is
 * pushed once after the consent gate and never refreshed; `filled` reports
 * like a GAM fill (B10 `impression` with `ssp: "adsense"`), `unfilled` or no
 * answer in 10 s collapses the container (ADR-032). GPT and Prebid never load
 * for them. A unit with any other demand renders nothing.
 * Outstream video with the publisher's own `vastTags` plays those directly.
 *
 * B10 (Atlas ch.4, `POST {origin}/v1/e`), `slot` = the unit's `key`
 * (`banner.top`, not `banner`): for a GAM slot, `viewable`+`impression` fire
 * only once GAM FILLED it and it then met MRC viewability (>=50% visible, 1s;
 * 2s for video), with `revenueMicros`/`ssp`/`size`/`hbPb` when a Prebid bid
 * won; a `click` when the reader clicks into the ad's iframe. `unitType` is
 * `banner` | `native` | `corner_slider` | `video`. Video wins report through
 * wireVideoBeacon() (start, quartiles, complete). Each auction's bids go to
 * `POST {origin}/v1/bids` once GAM has answered (Atlas ch.14). Not wired for
 * `audio`/read-aloud units - ReadAloudWidget speaks its own, entirely separate
 * Publisher Event vocabulary (widgets/readAloud/events/ReadAloudWidgetEvents.js);
 * wiring B10 for it is a future follow-on, not part of this milestone.
 *
 * `native` units render differently from every other type: `unit.divId`
 * names the ARTICLE WRAPPER, not a single ad slot - renderNativeUnit()
 * finds real paragraphs inside it and inserts `placement.count` ads spaced
 * `placement.minParagraphSpacing` paragraphs apart, each its own
 * independent DisplayWidget instance (own beacon requestId included).
 *
 * `banner` units with `placement.sticky === true` render differently again:
 * mountStickyBanner() creates a fixed-position container itself (assigned
 * `id = unit.divId`, so anything reading that id downstream still finds
 * it), reserves real page space on the top/bottom edges (or width-gates a
 * left/right rail, reserving nothing - see MIN_SIDE_RAIL_VIEWPORT) so the
 * publisher's own layout never breaks, and releases that reservation when
 * the widget's own dismissible close button is used. Ported from
 * playbid-sdk-plugin's own ad-units.js renderBanner(), verified against the
 * same code running live on jobopting.com/job-search-2026.html.
 *
 * `corner_slider` units are the same kind of self-injected floating widget,
 * ported from ad-units.js's renderSlider() - fixed to `placement.corner`
 * (`bottom-right`/`bottom-left`), hidden via `placement.animation`
 * (`slide`/`fade`) until the page has scrolled `placement.scrollTriggerPct`%
 * of its height, then revealed once (see mountCornerSlider()). Respects
 * `prefers-reduced-motion`. Its beacons carry `unitType: 'corner_slider'`.
 *
 * `outstream_video` units read `placement.mode`: `"in-content"` (default -
 * self-injected into the page's real content, see
 * createInContentVideoContainer()/mountInContentVideo() - no
 * publisher-placed div required or looked up) or `"sticky"` - a
 * self-injected floating player at a fixed bottom-right position from the
 * start, ported from ad-units.js's own `u.placement === "sticky"` branch of
 * renderVideo() (see mountStickyVideo()). An `"in-content"` video ALSO
 * transitions live, verified against jobopting.com/job-search-2026.html:
 * once its container scrolls above the viewport it sticks to that same
 * floating position (gaining a close button, reserving its own height so
 * the page doesn't jump), and unsticks back to normal in-place flow when
 * scrolled back into view - see mountInContentVideo(). The same <video>
 * element persists through the transition, so playback is never
 * interrupted.
 * `playback.muted`/`playback.autoplay` (both default true unless explicitly
 * `false`) apply throughout. Both modes also wire B10 (`unitType: 'video'`) -
 * see wireVideoBeacon(): `click`/`error` and the VAST milestones
 * (`vast_start`/`vast_first_quartile`/`vast_midpoint`/`vast_third_quartile`/
 * `vast_complete`, mirroring TrackingManager's own quartile math), plus
 * `viewable`/`impression` at a 2000ms MRC dwell (B10's own "2 s for video",
 * vs. 1000ms for every other unit type). `audio` isn't wired yet.
 *
 * `in_text` and `contextual_commerce` units (ADR-030) both send the
 * article's text to the edge (`POST /v1/intext/keywords`,
 * `POST /v1/commerce/products`) and render what comes back: in-text links up
 * to `inText.maxLinks` phrases inside the article's own paragraphs
 * (mountInText()); commerce self-injects a block of up to
 * `commerce.maxCards` product cards after the fifth real paragraph
 * (mountCommerce()). Both fire a B10 `impression` per MRC-viewable ad and
 * never a click beacon - their links are `/v1/c/` redirects that count the
 * click themselves (see wireContextualBeacon()). `rsoc` units (ADR-031)
 * self-inject a "Related searches" box of up to `rsoc.count` sponsored
 * searches (`POST /v1/rsoc/terms`) at `rsoc.position` (mountRelatedSearch()),
 * beaconed the same way. All three widgets share the one
 * `playbid-contextual.umd.js` bundle.
 *
 * `chat_sponsored` units (ADR-031) switch the read-aloud card's ask box onto
 * `POST /v1/chat`; without one it answers from the article and never calls
 * that endpoint. A sponsored answer card fires a B10 `impression` when
 * viewable, never a click beacon (wireChatSponsoredBeacon()). On a page with
 * a `chat_sponsored` unit but no `audio` unit, the card mounts with no ad
 * breaks (mountChatSponsored()).
 *
 * `audio` units never render a standalone audio-ad player - mountReadAloud()
 * self-injects the "Listen & Explore" read-aloud widget instead, after the
 * second real paragraph found on the page (findArticleParagraphs(), the same
 * heuristic renderNativeUnit() uses, scanning `document.body` since there is
 * no publisher-marked article wrapper for this widget to rely on), reading
 * every real paragraph found. The unit's own `positions.preRoll/midRoll/
 * postRoll` and `vastTags` become the widget's `adBreaks` config, not
 * playable content of their own - ReadAloudAdBreakRunner runs each break
 * through a disposable, internal AudioWidget instance (see
 * widgets/readAloud/ads/ReadAloudAdBreakRunner.js), so boot() never fetches
 * `playbid-audio.umd.js` separately for this - ReadAloudWidget's own bundle
 * already carries that code. A page with no real paragraph content silently
 * renders nothing, same "never throw into the host page" convention as
 * every other unit here.
 *
 * Every unit, regardless of type, is also gated on `unit.devices` (an
 * allow-list of `"desktop"`/`"tablet"`/`"mobile"`, checked once against the
 * current viewport width) - see deviceAllowed()/currentDevice() above
 * widgetTypesFor(). A unit this device isn't targeted for never renders,
 * and if EVERY unit needing a given widget type is excluded for this
 * device, that widget's script is never even fetched.
 */
export async function boot({ siteKey, apiBase, cdnBase, prebidSrc } = {}) {
  // The one line that answers "is the CDN actually serving what I just
  // deployed?" - always logged (not gated on a debug flag), since a stale
  // deploy is exactly the kind of thing that's invisible until you go
  // looking for it. `BUILD_ID` (`<version>+<gitSha>`) changes on every real
  // build, unlike `VERSION` alone, which only changes when someone bumps
  // package.json.
  console.info(
    `[PlaybidSDK] boot() build ${BUILD_ID} (channel=${config.env}, built ${SDK_BUILT_AT ?? 'unknown'})`,
  );

  const { configOrigin, scriptBase } = resolveBases({ apiBase, cdnBase });
  if (!configOrigin || !scriptBase) {
    console.warn('[PlaybidSDK] boot(): could not resolve a CDN base - pass cdnBase explicitly');
    return null;
  }

  // B1+B2, fired together (Atlas ch.4 B1+B2) - independent failure per call,
  // see fetchSdkConfig()/fetchTelemetryAuth() above.
  const [sdkConfigResult, telemetryAuth] = await Promise.all([
    fetchSdkConfig(configOrigin, siteKey).catch((error) => {
      console.warn('[PlaybidSDK] boot(): failed to fetch sdk-config', error);
      return null;
    }),
    fetchTelemetryAuth(configOrigin, siteKey),
  ]);

  if (!sdkConfigResult) return null;
  const bootConfig = { ...sdkConfigResult, telemetryAuth };
  shareTelemetrySettings(configOrigin, siteKey, telemetryAuth);

  const neededTypes = [...widgetTypesFor(bootConfig?.units)].filter(
    (type) => !isAlreadyLoaded(type),
  );
  // Several types can share one bundle (see WIDGET_TYPE_TO_BUNDLE), which is
  // fetched once however many of its types the page needs.
  const neededBundles = [...new Set(neededTypes.map(bundleFor))];
  // Styles load alongside (not after) each bundle's script - both are awaited
  // together here so every widget's CSS is already applied before
  // renderUnit() constructs the first instance, the same reason the script
  // itself is awaited before rendering starts.
  await Promise.all(
    neededBundles.flatMap((bundle) => [
      loadWidgetScript(scriptBase, bundle),
      loadWidgetStyles(scriptBase, bundle),
    ]),
  );

  // `units` rides along so a unit's render function can reason about the
  // OTHER units in this same response (e.g. mountCornerSlider() checking
  // for a sticky side rail to avoid overlapping) - never mutated, just read.
  const bases = {
    configOrigin,
    scriptBase,
    siteKey,
    units: bootConfig?.units,
    // The whole listenExplore block (layout/position/suggestions/enabled
    // flags), read verbatim off B1's response - see mountReadAloud() for
    // what's actually consumed from it today.
    listenExplore: bootConfig?.listenExplore,
    // One per page load. Lazy: GPT and Prebid are fetched only when the first
    // GAM slot registers, so a page with no GAM-served unit loads neither.
    adServer: new PageAdServer({
      config: bootConfig,
      // Playbid's own Prebid build, from the same origin as the config.
      prebidSrc: prebidSrc ?? `${configOrigin}/${PREBID_BUNDLE_PATH}`,
      sendBids: (batch) => sendBidBatch(configOrigin, siteKey, batch),
      onDiagnostic: (message, detail) => console.debug(`[PlaybidSDK] ${message}`, detail ?? ''),
    }),
    // One per page load, lazy too: adsbygoogle.js loads only when the first
    // `demand: "adsense"` unit fills, so a page without one never fetches it.
    adSense: new AdSensePage({
      config: bootConfig,
      onDiagnostic: (message, detail) => console.debug(`[PlaybidSDK] ${message}`, detail ?? ''),
    }),
  };
  for (const unit of bootConfig?.units ?? []) {
    const type = FORMAT_TO_WIDGET_TYPE[unit?.format];
    if (type) renderUnit(unit, type, bases);
  }

  return bootConfig;
}
